import {useQuery, gql} from '@apollo/client'
import useRouteBasedSkip from '../../hooks/useRouteBasedSkip'

export const GET_ADMIN_PERMISSION = gql`
  query GetAdminPermission {
    adminPermission {
      stateList {
        STATE_ID
        STATE_NAME
        STATE_ABBREVIATION
        IS_ACTIVE
        IS_PERMISSIONED
      }
      filters {
        title
        custom_title
        sections {
          key
          title
          custom_title
        }
        data {
          FILTER_ID
          FILTER_KEY
          FILTER_NAME
          FILTER_CUSTOM_NAME
          FILTER_CATEGORY_ID
          IS_ACTIVE
          IS_PERMISSIONED
        }
        filter_category_name {
          FILTER_CATEGORY_ID
          FILTER_CATEGORY_NAME
          FILTER_CATEGORY_CUSTOM_NAME
        }
      }
      usecaseList {
        title
        domains {
          name
          custom_name
        }
        topics {
          name
          custom_name
        }
        subTopics {
          name
          custom_name
        }
        useCases {
          id
          name
          custom_name
        }
      }
      personaList {
        data {
          type
          title
          custom_title
          data {
            name
            custom_name
          }
        }
      }
      pointOfInterestList {
        POI_ID
        POI_NAME
        POI_CUSTOM_NAME
        IS_PERMISSIONED
      }
      permissionList {
        dataAggregationPermissions
        trendAnalysisPermissions
        exportFlagPermissions
        chatbotFlagPermissions
        chatbotExportFlagPermissions
        chatbotCopyFlagPermissions
        benefitsEligibilityPermissions
        workforceRecruitmentPermissions
        pointOfInterestList
        usecasePermissions
        personaFlagPermissions
        userfeedbackFlagPermissions
      }
    }
  }
`

export const useGetAdminPermission = (statesConfig) => {
  const shouldSkip = useRouteBasedSkip('/adminpanel')

  const {
    loading: adminPermissionLoading,
    error: adminPermissionError,
    data,
    refetch: refetchAdminPermission
  } = useQuery(GET_ADMIN_PERMISSION, {
    context: {clientName: 'adminPanel'},
    skip: shouldSkip,
    fetchPolicy: 'cache-first'
  })

  // Return shouldSkip so callers know if query is active
  const isQueryActive = !shouldSkip

  const filters =
    adminPermissionLoading || !data?.adminPermission
      ? undefined
      : (data.adminPermission.filters?.data || data.adminPermission.filters)?.map((filter) => {
          const categoryName =
            data.adminPermission.filters?.filter_category_name?.find(
              (category) => category.FILTER_CATEGORY_ID === filter.FILTER_CATEGORY_ID
            )?.FILTER_CATEGORY_NAME || ''

          return {
            title: filter.FILTER_NAME,
            title_head: categoryName,
            FILTER_ID: filter.FILTER_ID,
            FILTER_CATEGORY_ID: filter.FILTER_CATEGORY_ID,
            IS_ACTIVE: filter.IS_ACTIVE,
            FILTER_KEY: filter.FILTER_KEY,
            FILTER_CUSTOM_NAME: filter.FILTER_CUSTOM_NAME,
            IS_PERMISSIONED: filter.IS_PERMISSIONED
          }
        }) || []

  const stateList = data?.adminPermission?.stateList
  let stateListFilter = []
  if (statesConfig.entireUnitedStateLabel) {
    if (stateList?.length === statesConfig.totalStates) {
      // Create a copy before modifying since Apollo data is immutable
      stateListFilter = [{STATE_ID: 0, STATE_NAME: statesConfig.entireUnitedStateLabel, IS_ACTIVE: true}, ...stateList]
    } else if (stateList) {
      stateListFilter = [...stateList]
    }
    stateListFilter = stateListFilter.map(({STATE_NAME: title, ...rest}) => ({
      title,
      ...rest
    }))
  }
  const pointOfInterestFilter =
    adminPermissionLoading || !data?.adminPermission
      ? undefined
      : data.adminPermission.pointOfInterestList?.map(({POI_NAME: title, ...rest}) => ({
          title,
          ...rest
        })) || []
  const permissionList = data?.adminPermission?.permissionList || []
  return {
    adminPermissionLoading,
    adminPermissionError,
    filters,
    data,
    stateListFilter,
    pointOfInterestFilter,
    permissionList,
    refetchAdminPermission,
    isQueryActive
  }
}
